/*
 * Показ чисел и дней личного учёта.
 *
 * Здесь только показ. Всё, что считается, считает сервер — иначе те же
 * формулы пришлось бы держать в двух местах, и однажды они разошлись бы
 * на копейку, а потом на месячный бюджет.
 */

const MONTH_NAMES = [
	'январь',
	'февраль',
	'март',
	'апрель',
	'май',
	'июнь',
	'июль',
	'август',
	'сентябрь',
	'октябрь',
	'ноябрь',
	'декабрь'
];

const MONTH_IN = [
	'января',
	'февраля',
	'марта',
	'апреля',
	'мая',
	'июня',
	'июля',
	'августа',
	'сентября',
	'октября',
	'ноября',
	'декабря'
];

/** Число из строки протокола. Пустое и мусорное дают `null`, а не ноль. */
export function toNumber(value: string | null | undefined): number | null {
	if (value === null || value === undefined || value.trim() === '') return null;
	const parsed = Number(value.replace(',', '.'));
	return Number.isFinite(parsed) ? parsed : null;
}

/** Деньги с валютой кошелька: «1 234,56 ₽». */
export function money(value: string | number | null | undefined, currency = 'RUB'): string {
	const parsed = typeof value === 'number' ? value : toNumber(value ?? null);
	if (parsed === null) return '—';
	try {
		return new Intl.NumberFormat('ru-RU', {
			style: 'currency',
			currency,
			maximumFractionDigits: 2
		}).format(parsed);
	} catch {
		// Валюта может быть нестандартной — USDT валютой ISO не является.
		return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(parsed)} ${currency}`;
	}
}

/** Со знаком: доход и расход должны читаться, не вглядываясь. */
export function signedMoney(value: string | null | undefined, currency = 'RUB'): string {
	const parsed = toNumber(value ?? null);
	if (parsed === null) return '—';
	return `${parsed > 0 ? '+' : ''}${money(parsed, currency)}`;
}

export function percent(value: string | null | undefined): string {
	const parsed = toNumber(value ?? null);
	if (parsed === null) return '—';
	return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(parsed)} %`;
}

/** Знак числа: для подсветки прихода и расхода. */
export function sign(value: string | null | undefined): 'plus' | 'minus' | 'zero' {
	const parsed = toNumber(value ?? null);
	if (parsed === null || parsed === 0) return 'zero';
	return parsed > 0 ? 'plus' : 'minus';
}

/* --- Дни и месяцы --------------------------------------------------------- */

/** Сегодняшний день строкой «2026-08-17» — в этом виде он уходит на сервер. */
export function todayIso(): string {
	const now = new Date();
	return [
		now.getFullYear(),
		String(now.getMonth() + 1).padStart(2, '0'),
		String(now.getDate()).padStart(2, '0')
	].join('-');
}

/** Месяц сегодняшнего дня: «2026-08». */
export function currentMonth(): string {
	return todayIso().slice(0, 7);
}

/** Сдвинуть «2026-08» на несколько месяцев. */
export function shiftMonth(month: string, delta: number): string {
	const [year, index] = month.split('-').map(Number) as [number, number];
	const moved = new Date(Date.UTC(year, index - 1 + delta, 1));
	return moved.toISOString().slice(0, 7);
}

/** Первый и последний день месяца — границы запроса записей. */
export function monthRange(month: string): { from: string; to: string } {
	const [year, index] = month.split('-').map(Number) as [number, number];
	const last = new Date(Date.UTC(year, index, 0)).getUTCDate();
	return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/** «Август 2026» — заголовок отчёта. */
export function monthTitle(month: string): string {
	const [year, index] = month.split('-').map(Number) as [number, number];
	const name = MONTH_NAMES[index - 1] ?? month;
	return `${name[0]!.toLocaleUpperCase('ru')}${name.slice(1)} ${year}`;
}

/** «авг.» — подпись столбика в годовом ряду. */
export function monthShort(month: string): string {
	const index = Number(month.split('-')[1]);
	return `${(MONTH_NAMES[index - 1] ?? '').slice(0, 3)}.`;
}

/**
 * Заголовок дня в ленте: «Сегодня», «Вчера» или «12 августа».
 *
 * Год добавляется только к чужим годам: писать «12 августа 2026 года» в
 * ленте текущего года — лишний шум в каждой строке.
 */
export function dayTitle(iso: string): string {
	const today = todayIso();
	if (iso === today) return 'Сегодня';

	const [year, month, day] = iso.split('-').map(Number) as [number, number, number];
	const yesterday = new Date();
	yesterday.setDate(yesterday.getDate() - 1);
	if (iso === yesterday.toISOString().slice(0, 10)) return 'Вчера';

	const name = MONTH_IN[month - 1] ?? '';
	const suffix = year === new Date().getFullYear() ? '' : ` ${year}`;
	return `${day} ${name}${suffix}`;
}

/** Русское склонение по числу: `plural(2, 'месяц', 'месяца', 'месяцев')`. */
export function plural(n: number, one: string, few: string, many: string): string {
	const mod10 = n % 10;
	const mod100 = n % 100;
	if (mod10 === 1 && mod100 !== 11) return one;
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
	return many;
}

/** «раз в месяц», «раз в 2 недели» — подпись регулярного правила. */
export function periodTitle(period: 'day' | 'week' | 'month' | 'year', step: number): string {
	const words = {
		day: ['день', 'дня', 'дней'],
		week: ['неделю', 'недели', 'недель'],
		month: ['месяц', 'месяца', 'месяцев'],
		year: ['год', 'года', 'лет']
	}[period];

	if (step === 1) return `раз в ${words[0]}`;
	return `раз в ${step} ${plural(step, words[0]!, words[1]!, words[2]!)}`;
}
